import Fields from 'gql-query-builder/build/Fields';
import IQueryBuilderOptions from 'gql-query-builder/build/IQueryBuilderOptions';
import Utils from 'gql-query-builder/build/Utils';
import OperationType from 'gql-query-builder/build/OperationType';
import VariableOptions from 'gql-query-builder/build/VariableOptions';
import IQueryAdapter from 'gql-query-builder/build/adapters/IQueryAdapter';
import {QueryBuilderOptions} from './query-builder-options';

type Configuration = { [key: string]: unknown };

export class QueryAdapter implements IQueryAdapter {
  private readonly variables!: any | undefined;
  private fields: Fields | undefined;
  private operation!: string;
  private readonly config: Configuration;

  private readonly operationParameter?: string;

  constructor(options: QueryBuilderOptions, configuration?: Configuration) {
    // Default configs
    this.config = {
      operationName: ''
    };
    if (configuration) {
      Object.entries(configuration).forEach(([key, value]) => {
        this.config[key] = value;
      });
    }

    if (Array.isArray(options)) {
      this.variables = Utils.resolveVariables(options);
    } else {
      this.variables = options.variables;
      this.fields = options.fields || [];
      this.operation = options.operation;
      this.operationParameter = options.operationParameter;
    }
  }

  // kicks off building for a single query
  public queryBuilder() {
    return this.operationWrapperTemplate(this.operationTemplate(this.variables));
  }

  // if we have an array of options, call this
  public queriesBuilder(queries: IQueryBuilderOptions[]) {
    const content = () => {
      const tmpl: string[] = [];
      queries.forEach((query) => {
        if (query) {
          this.operation = query.operation;
          this.fields = query.fields;
          tmpl.push(this.operationTemplate(query.variables));
        }
      });
      return tmpl.join(' ');
    };
    return this.operationWrapperTemplate(content());
  }

  // Convert object to argument and type map. eg: ($id: Int)
  private queryDataArgumentAndTypeMap(): string {
    let variablesUsed = this.variables;

    if (this.fields && typeof this.fields === 'object') {
      variablesUsed = {
        ...Utils.getNestedVariables(this.fields),
        ...variablesUsed
      };
    }
    if (variablesUsed && Object.keys(variablesUsed).length > 0) {
      const variablesAsString = Object.keys(variablesUsed).reduce(
        (dataString, key, i) => {
          return `${dataString}${i !== 0 ? ', ' : ''}$${key}: ${Utils.queryDataType(variablesUsed[key])}`
        }, ''
      );
      return `(${variablesAsString})`
    }
    return '';
  }

  private operationWrapperTemplate(content: string): { variables: VariableOptions; query: string } {
    let query = `${OperationType.Query} ${this.queryDataArgumentAndTypeMap()} { ${content} }`;
    const operationName = this.config.operationName !== '' ? ` ${this.config.operationName}` : '';
    query = query.replace('query', `query${operationName}`);
    return {
      query,
      variables: Utils.queryVariablesMap(this.variables, this.fields)
    };
  }

  // If passed in the options, the operation parameter (ES query) will be inserted in the query
  private operationTemplate(variables: VariableOptions | undefined) {
    const operatorParameter = this.operationParameter ? `(${this.operationParameter})` :
      variables ? Utils.queryDataNameAndArgumentMap(variables) : '';
    const operation = `${this.operation} ${operatorParameter}`;
    if (this.fields && this.fields.length > 0) {
      return `${operation} { ${Utils.queryFieldsMap(this.fields)} }`
    }
    return `${operation}`;
  }
}
